import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';

import Page from '@/components/page/Page';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/common/data-table/data-table';
import { ShoppingCart, Sparkles, Package } from 'lucide-react';

import {
  mockInventory,
  mockItems,
  calculateMaterialRequirements,
  type InventoryItem,
} from '../data/mockData';

interface EnrichedInventoryItem extends InventoryItem {
  item: (typeof mockItems)[0] | undefined;
  optimalLevel: number;
  aiForecast: string;
  needsAction: boolean;
}

export function InventoryPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);

  // Enrich inventory data
  const enrichedInventory: EnrichedInventoryItem[] = useMemo(() => {
    const requirements = calculateMaterialRequirements();
    return mockInventory.map((inv, index) => {
      const item = mockItems.find((i) => i.id === inv.itemId);
      const req = requirements.find((r) => r.item.id === inv.itemId);

      const forecasts = [
        'Reorder in 2 weeks',
        'Stock sufficient for Q1',
        'Consider bulk order',
        'Demand increasing',
        'Stable demand expected',
      ];

      const optimalLevel = inv.reorderPoint + inv.safetyStock;
      const needsAction =
        inv.availableStock < inv.reorderPoint || (req?.shortage ?? 0) > 0;

      return {
        ...inv,
        item,
        optimalLevel,
        aiForecast: forecasts[index % forecasts.length],
        needsAction,
      };
    });
  }, []);

  const columns: ColumnDef<EnrichedInventoryItem>[] = useMemo(
    () => [
      {
        accessorKey: 'item.name',
        header: 'Item Name',
        cell: ({ row }) => {
          const item = row.original.item;
          return (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                <Package className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <div className="font-medium">{item?.name ?? 'Unknown'}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {item?.sku}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'currentStock',
        header: 'Current Stock',
        cell: ({ row }) => {
          const inv = row.original;
          const { optimalLevel, currentStock } = inv;
          const ratio = currentStock / optimalLevel;
          const percentage = Math.min(ratio * 100, 100);

          // Determine color based on ratio
          // < 0.5 = red (low stock)
          // 0.5 - 1.2 = green (good stock)
          // > 1.2 = orange (oversupply)
          const getBarColor = () => {
            if (ratio < 0.5) return 'bg-rose-500';
            if (ratio > 1.2) return 'bg-amber-500';
            return 'bg-emerald-500';
          };

          const getTextColor = () => {
            if (ratio < 0.5) return 'text-rose-600';
            if (ratio > 1.2) return 'text-amber-600';
            return 'text-foreground';
          };

          return (
            <div className="flex items-center gap-2">
              <span className={`font-light ${getTextColor()}`}>
                {currentStock.toLocaleString()}
              </span>
              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${getBarColor()}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'optimalLevel',
        header: 'Optimal Level',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.optimalLevel.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'aiForecast',
        header: 'AI Forecast',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-sm text-muted-foreground">
              {row.original.aiForecast}
            </span>
          </div>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        cell: () => {
          return (
            <Button size="sm" variant="outline" className="h-7 text-xs">
              <ShoppingCart className="h-3 w-3 mr-1.5" />
              Buy product
            </Button>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: enrichedInventory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <Page
      title="Inventory Planning"
      subtitle="Monitor stock levels and plan reorders"
    >
      {/* Action Section */}
      <Card className="mb-6">
        <div className="px-4 py-3 border-b">
          <h2 className="font-semibold text-sm">Action Items</h2>
        </div>
        <CardContent className="p-0">
          <div className="divide-y">
            {/* AI Recommendation - Main Card */}
            <div className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="px-2 py-1 rounded text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-400">
                    Recommendation
                  </span>
                  <span className="font-medium">
                    Reorder before next week to meet Advanced Microchip X1
                    demand
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <div>Demand forecast: 10,000 Microchips needed</div>
                  <div>
                    Multiple raw materials required to meet production demand
                  </div>
                </div>
              </div>
              <Button
                onClick={() =>
                  navigate({ to: '/procurement-demo/procurement-plan' })
                }
                className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Review with AI
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable table={table} />
        </CardContent>
      </Card>
    </Page>
  );
}
